import { isClonedVoiceCode } from '@vbee-holding/aivoice-shared-lib';
import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import moment from 'moment';
import { Button, Checkbox, Typography } from '@mui/material';
import {
  WarningRounded as WarningRoundedIcon,
  CallMerge as MergeIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { OrderedSet } from 'immutable';
import { BREAK_LINE_DELIMITER } from '@src/constants';
import {
  CONVERTING_STATUS,
  DIRECTION,
  MAX_CHARACTER_PER_BLOCK,
  BLOCK_STATUS,
} from '@src/constants/project';
import {
  ContentState,
  Editor,
  EditorState,
  Modifier,
  getDefaultKeyBinding,
} from 'draft-js';
import sanitize from 'sanitize-html';
import Tooltip from '@src/components/Tooltip';
import { COLOR } from '@src/styles/color';
import addBlock from '@src/assets/icons/add-block.svg';
import {
  BREAK_TIME_REGEX,
  DEFAULT_BREAK_TIME,
  DEFAULT_SPEED,
  VOICE_OWNERSHIP,
} from '@src/constants/voice';
import { v4 as uuidv4 } from 'uuid';
import { countTextLength } from '@src/services/tts';
import useIsAutoSaveOverLimit from '@src/hooks/useIsAutoSaveOverLimit';
import BreakTimeHighlight from '@src/containers/TTS/BreakTimeHightLight';
import BlackWordHighLight from '@src/containers/TTS/BlackWordHighLight';
import {
  generateDecorator,
  generateCompositeDecorator,
} from '@src/services/editor';
import { checkBrowser } from '@src/utils/browser';
import { MAX_CHARACTER_LENGTH, VALID_LETTERS } from '@src/constants/tts';
import actions from '@src/redux/actions';
import {
  downloadFileByUrl,
  // downloadFileUsingUrl,
  getFileName,
} from '@src/utils/file';
import { LANDING_PAGE_URL } from '@src/configs';
import { CATEGORY_TYPE, PLATFORM_TYPE } from '@src/constants/support';
import { getPresignedAudioUrl } from '@src/apis/requests';
import { trackBlockAudioDownloaded } from '@src/services/moengage';
import {
  getBlockStatus,
  isBlockChanged,
  formatInvalidTexts,
  checkBlockTextError,
} from '@src/services/project';
import VoiceSelectionDialog from '@src/containers/VoiceSelectionDialog';
import CommonButton from '@src/new-components/Button';
import NewTypography from '@src/new-components/Typography';
import {
  StyledAddNewBlockZone,
  StyledBlockEditor,
  StyledContentBlock,
  StyledContentBlockWrapper,
  StyledInfoBlock,
  StyledOrdinalNumberBlock,
} from './index.style';
import MoreOptionBlock from '../../component/MoreOptionBlock';
import ConfigOptionBlock from '../../component/ConfigOptionBlock';
import CreditsCount from '../../component/CreditsCount';
import EditNameBlock from './EditNameBlock';
import NewSynthesisButton from '../../component/NewSynthesisButton';

const ContentBlock = ({
  blockId,
  block,
  characterExceed,
  onAddNewParagraphs,
  isLastBlock,
  isFocusing,
  onFocusingBlock,
  onSaveProject,
  voiceDefault,
  isPlayingBlock,
  isDownloading,
  onOpenOutOfCreditsDialog,
  onOpenWrongSyntaxDialog,
  onOpenWrongSyntaxDialogV2,
  onOpenDiscardedVoiceCloningDialog,
  checkValidCreateSynthesis,
  isHighlight,
  handlePlayAudio,
  projectId,
  originalBlock,
  onConvertBlockToAudio,
  audioType,
  setDeleteDialogData,
  onMergeBlock,
  nextBlock,
  areBlocksConsecutive = true,
  enableCreditUsagePreview,
  onDuplicateBlock,
  onReSynthesizeBlock,
  onDragEnd,
  disabledMoveUp,
  blockAudioCurrentTime,
  blockAudioDuration,
  handleSeekedInBlock,
  currentPlayingBlockId,
  foundBlackWords = [],
}) => {
  const { t, i18n } = useTranslation();

  const { language } = i18n;
  const inputRef = useRef(null);
  const blockRef = useRef(null);
  const userId = useSelector((state) => state.auth.user?.id);

  const blockIds = useSelector((state) => state.project.multiBlock?.blockIds);

  const indexBlock = useMemo(() => {
    const idx = blockIds.indexOf(blockId) + 1;
    return idx;
  }, [blockIds, blockId]);

  const { filterVoice } = useSelector((state) => state.voice);
  const dispatch = useDispatch();
  // Disable this block's synthesis when over the character limit under
  // auto-save (the project can't be persisted in that state).
  const isAutoSaveOverLimit = useIsAutoSaveOverLimit();

  const {
    voiceCode = voiceDefault?.code,
    text = '',
    speed = DEFAULT_SPEED,
    breakTime = DEFAULT_BREAK_TIME,
    audioLink,
    status,
    requestId,
    showSuccessIcon,
    audioExpiredAt,
    voice = voiceDefault,
    isBlockEditting,
    title = '',
  } = block;

  // Seperately destructuring hasEmphasis and emphasis since hasEmphasis is dependent on voice?.hasEmphasis
  const { hasEmphasis = voice?.hasEmphasis, emphasis = 0 } = block;

  const maxCharLimit = MAX_CHARACTER_PER_BLOCK;

  const blockStatus = getBlockStatus({
    block,
    originalBlock,
    audioType,
    maxCharLimit,
    blackWordsList: foundBlackWords,
    voice,
  });

  const {
    exceedMaxChar: exceedBlockMaxChar,
    invalidCharacterLengthTexts,
    hasInvalidCharacterLength,
    hasInvalidCharacters,
    hasBlackWords,
  } = checkBlockTextError(text, maxCharLimit, foundBlackWords, voice);

  const getBlockErrorConfig = () => {
    const sanitizeOptions = {
      allowedTags: ['br', 'p', 'strong'],
      allowedAttributes: {
        p: ['style'],
        br: ['style'],
      },
    };

    if (exceedBlockMaxChar) {
      return {
        message: t('exceedBlockMaxChar', {
          maxCredits: maxCharLimit.toLocaleString(language),
        }),
        sanitizeOptions,
      };
    }
    if (hasInvalidCharacterLength) {
      return {
        message: t('invalidCharLengthMessage', {
          invalidTexts: formatInvalidTexts(invalidCharacterLengthTexts),
          maxCharacterLength: MAX_CHARACTER_LENGTH - 1,
        }),
        sanitizeOptions,
      };
    }
    if (hasInvalidCharacters) {
      return {
        message: t('invalidCharacter', { invalidText: text.trim() }),
        sanitizeOptions,
      };
    }
    if (hasBlackWords) {
      return {
        message: t('sentenceHasBlackWordsDescription'),
        sanitizeOptions,
      };
    }
    return { message: '', sanitizeOptions };
  };

  const isSynthesizing =
    isDownloading || status === CONVERTING_STATUS.IN_PROGRESS;
  const isNextBlockSynthesizing =
    nextBlock?.status === CONVERTING_STATUS.IN_PROGRESS;
  const hasSuccessSynthesized =
    audioLink && status === CONVERTING_STATUS.SUCCESS;
  const isAudioExpired = audioExpiredAt && moment().isAfter(audioExpiredAt);
  const showReSynthesize =
    status === CONVERTING_STATUS.SUCCESS ||
    status === CONVERTING_STATUS.FAILURE;

  const costsCredit =
    (voice?.creditFactor || voiceDefault?.creditFactor) * countTextLength(text);
  const reSynthesizeTooltip = enableCreditUsagePreview
    ? t('costsCredit', { value: costsCredit?.toLocaleString(language) })
    : t('convertToAudioToListen');

  const isThisBlockActive = currentPlayingBlockId === blockId;
  const popoverCurrentTime = isThisBlockActive ? blockAudioCurrentTime : 0;
  const popoverDuration = isThisBlockActive ? blockAudioDuration : 0;

  const cannotMerge =
    !nextBlock ||
    isNextBlockSynthesizing ||
    isSynthesizing ||
    !areBlocksConsecutive;

  const [content, setContent] = useState(
    EditorState.createEmpty(
      generateDecorator(BREAK_TIME_REGEX, BreakTimeHighlight),
    ),
  );
  const [openVoices, setOpenVoices] = useState(false);

  const [openPopperBlockOption, setOpenPopperBlockOption] = useState(false);

  const handleOpenVoices = () => {
    const voiceOwnershipFilter = filterVoice?.voiceOwnership;
    const isClonedVoice = isClonedVoiceCode(voice?.code);
    let voiceOwnership = isClonedVoice
      ? VOICE_OWNERSHIP.PERSONAL
      : VOICE_OWNERSHIP.VBEE;

    if (
      isClonedVoice &&
      (voiceOwnershipFilter === VOICE_OWNERSHIP.COMMUNITY ||
        voice?.userId !== userId)
    )
      voiceOwnership = VOICE_OWNERSHIP.COMMUNITY;

    if (!voiceOwnershipFilter || voiceOwnershipFilter !== voiceOwnership)
      dispatch(
        actions.voice.changeFilterVoice({
          ...filterVoice,
          voiceOwnership,
        }),
      );

    setOpenVoices(true);
  };
  const handleCloseVoices = () => setOpenVoices(false);

  const handleOpenDeleteDialog = () => {
    setDeleteDialogData(blockId);
  };

  const getSelectionOffset = (editorState) => {
    // Get the current content and selection states
    const contentState = editorState.getCurrentContent();
    const selectionState = editorState.getSelection();

    // Get the key of the block where the selection starts
    const startKey = selectionState.getStartKey();

    // Get the offset within the starting block
    const startOffset = selectionState.getStartOffset();

    // Get a list of all content blocks
    const blockMap = contentState.getBlockMap();
    const blocks = blockMap.toList();

    let characterOffset = 0;

    // Find the index of the start block in the list
    const startBlockIndex = blocks.findIndex(
      (blockItem) => blockItem.getKey() === startKey,
    );

    // Iterate through all blocks before the start block and add their length + 1 (for the newline)
    for (let i = 0; i < startBlockIndex; i += 1) {
      characterOffset += blocks.get(i).getLength() + 1;
    }

    // Add the offset within the start block
    characterOffset += startOffset;

    return characterOffset;
  };

  // Check if cursor is in valid position for splitting
  const canSplitBlock = useMemo(() => {
    if (!text || isSynthesizing) return false;

    const offset = getSelectionOffset(content);

    // Must have text before and after cursor
    return offset > 0 && offset < text.length;
  }, [content, text, isSynthesizing, isLastBlock]);

  const isCheckBlockChanged = useMemo(() => {
    const withoutExtension = block?.audioLink?.split('.')?.pop();
    const currentBlock = {
      text: block?.text,
      voiceCode: block?.voiceCode,
      speed: block?.speed,
      audioType: withoutExtension,
      emphasis: block?.emphasis,
    };
    const blockOriginal = {
      text: originalBlock?.text,
      voiceCode: originalBlock?.voiceCode,
      speed: originalBlock?.speed,
      audioType,
      emphasis: originalBlock?.emphasis,
    };

    return isBlockChanged(currentBlock, blockOriginal);
  }, [block, originalBlock, audioType]);

  const isDisableConvertBtn = useMemo(
    () =>
      !text ||
      countTextLength(text) > maxCharLimit ||
      hasInvalidCharacterLength ||
      hasInvalidCharacters ||
      hasBlackWords ||
      isAutoSaveOverLimit,
    [
      text,
      maxCharLimit,
      hasInvalidCharacterLength,
      hasInvalidCharacters,
      hasBlackWords,
      isAutoSaveOverLimit,
    ],
  );

  const isDisablePlayAudio = useMemo(
    () => isCheckBlockChanged || !hasSuccessSynthesized || isAudioExpired,
    [isCheckBlockChanged, hasSuccessSynthesized, isAudioExpired],
  );

  const handleChangeContent = (newContent) => setContent(newContent);

  // Handle split block action
  const handleSplitBlock = () => {
    if (!canSplitBlock) return;

    const contentState = content.getCurrentContent();
    const plainText = contentState.getPlainText();
    const offset = getSelectionOffset(content);
    // Split text at cursor position
    const textBefore = plainText.substring(0, offset);
    const textAfter = plainText.substring(offset);
    // Check if cursor is at the start of a block

    // Update current block with text before cursor
    dispatch(
      actions.project.updateBlock(blockId, { text: textBefore?.trim() }),
    );

    // Update editor state for current block
    const newContentState = ContentState.createFromText(textBefore?.trim());
    const newEditorState = EditorState.createWithContent(
      newContentState,
      generateDecorator(BREAK_TIME_REGEX, BreakTimeHighlight),
    );
    handleChangeContent(newEditorState);

    // Create new block with text after cursor
    const newBlockId = uuidv4();
    const newBlock = {
      voice,
      voiceCode,
      speed,
      breakTime,
      id: newBlockId,
      text: textAfter?.trim(),
      blockStatus: BLOCK_STATUS.DRAFT,
      emphasis,
      hasEmphasis,
    };

    // Add new block after current block
    onAddNewParagraphs(blockId, [newBlock]);

    // Focus on new block after it's added
    setTimeout(() => {
      onFocusingBlock(newBlockId);
    }, 100);
  };

  // Handle adding new block via Ctrl+Enter (separate from split block feature)
  const handleAddNewBlockOnShortcut = () => {
    if (isSynthesizing) return;

    // Calculate timing for dubbing projects

    // Create new empty block with same config as current block
    const newBlockId = uuidv4();
    const newBlock = {
      voice,
      voiceCode,
      speed,
      breakTime,
      id: newBlockId,
      text: '',
      blockStatus: BLOCK_STATUS.DRAFT,
      emphasis,
      hasEmphasis,
    };

    // Add new block after current block
    onAddNewParagraphs(blockId, [newBlock]);

    // Focus on new block after it's added
    onFocusingBlock(newBlockId);
  };

  // Handle keyboard shortcuts for adding new block
  const keyBindingFn = (e) => {
    // Detect Ctrl+Enter (Windows/Linux) or Cmd+Enter (Mac)
    if (
      e.keyCode === 13 && // Enter key
      (e.ctrlKey || e.metaKey) // Ctrl or Cmd key
    ) {
      return 'add-new-block-command';
    }
    return getDefaultKeyBinding(e);
  };

  const handleKeyCommand = (command) => {
    if (command === 'add-new-block-command') {
      handleAddNewBlockOnShortcut();
      return 'handled';
    }
    return 'not-handled';
  };

  const handleChangeVoice = (newVoice) => {
    dispatch(
      actions.project.updateBlock(blockId, {
        hasEmphasis: newVoice?.hasEmphasis,
        emphasis: 0,
        voiceCode: newVoice?.code,
        blockStatus,
        voice: {
          code: newVoice?.code,
          name: newVoice?.name,
          roundImage: newVoice?.roundImage,
          language: {
            roundImage: newVoice?.language?.roundImage,
          },
          level: newVoice?.level,
          provider: newVoice?.provider,
          eolDate: newVoice?.eolDate,
          creditFactor: newVoice?.creditFactor,
          canonicalVoice: newVoice?.canonicalVoice,
          discardAt: newVoice?.discardAt,
          retentionDays: newVoice?.retentionDays,
          status: newVoice?.status,
          userId: newVoice?.userId,
          features: Array.isArray(newVoice?.features) ? newVoice.features : [],
          labels: newVoice?.labels,
        },
      }),
    );
  };

  const handleChangeSpeed = (field, value) => {
    dispatch(
      actions.project.updateBlock(blockId, { speed: value, blockStatus }),
    );
  };

  const handleChangeEmphasis = (field, value) => {
    dispatch(
      actions.project.updateBlock(blockId, { emphasis: value, blockStatus }),
    );
  };

  const handleChangeBreakTime = (value) => {
    const currentContent = content.getCurrentContent();
    const selection = content.getSelection();
    const label = `<break time=${value}s/>`;
    const textWithEntity = Modifier.insertText(
      currentContent,
      selection,
      label,
      OrderedSet.of('BOLD'),
    );

    dispatch(
      actions.project.updateBlock(blockId, {
        breakTime: value,
        text: textWithEntity.getPlainText(),
        blockStatus,
      }),
    );
    handleChangeContent(
      EditorState.push(content, textWithEntity, 'insert-characters'),
    );
  };

  const handleChangeText = (value) => {
    dispatch(
      actions.project.updateBlock(blockId, { text: value, blockStatus }),
    );
  };

  const handleEditorChange = (editorState) => {
    const selectionState = editorState.getSelection();
    if (!selectionState.toJS().hasFocus) return;
    const contentState = editorState.getCurrentContent();
    const plainText = contentState.getPlainText('\n');
    let newContent = editorState;

    const isContentChanged = !content
      .getCurrentContent()
      .equals(editorState.getCurrentContent());
    if (isContentChanged) {
      const currentStyle = editorState.getCurrentInlineStyle();
      if (currentStyle && currentStyle.size) {
        newContent = EditorState.setInlineStyleOverride(
          editorState,
          OrderedSet.of(),
        );
      }
      handleChangeContent(newContent);
      handleChangeText(plainText);
    } else {
      handleChangeContent(editorState);
    }
  };

  const handleAddNewParagraphs = () => {
    onAddNewParagraphs(blockId);
  };

  const handlePastedText = (txt) => {
    const splitParagraphs = txt.split(BREAK_LINE_DELIMITER);
    if (splitParagraphs.length > 1) {
      const selectionState = content.getSelection();
      const currentContent = content.getCurrentContent();

      // Insert pasted text at cursor position (or replace selected text)
      const textWithEntity = Modifier.replaceText(
        currentContent,
        selectionState,
        txt,
      );

      // Get the updated text with the pasted content
      const updatedText = textWithEntity.getPlainText('\n');
      const paragraphs = updatedText
        .split(BREAK_LINE_DELIMITER)
        .filter((p) => p.trim());

      // First paragraph is a combination of text before cursor + first part of pasted text
      const firstParagraphText = paragraphs.shift();

      // Create new blocks for the remaining paragraphs plus the text that was after the cursor
      const newBlocks = [];

      // Process middle paragraphs (if any)
      while (paragraphs.length > 1) {
        newBlocks.push({
          voice,
          voiceCode,
          speed,
          breakTime,
          id: uuidv4(),
          text: paragraphs.shift(),
          blockStatus: BLOCK_STATUS.DRAFT,
          emphasis,
          hasEmphasis,
        });
      }

      // Last paragraph combines the last part of pasted text with text after cursor
      if (paragraphs.length === 1) {
        newBlocks.push({
          voice,
          voiceCode,
          speed,
          breakTime,
          id: uuidv4(),
          text: paragraphs[0],
          blockStatus: BLOCK_STATUS.DRAFT,
          emphasis,
          hasEmphasis,
        });
      }

      // Update the editor state
      handleChangeContent(
        EditorState.createWithContent(
          ContentState.createFromText(firstParagraphText),
        ),
      );

      // Update current block with first paragraph text
      dispatch(
        actions.project.updateBlock(blockId, {
          text: firstParagraphText,
          voiceCode,
          speed,
          breakTime,
          blockStatus,
        }),
      );

      // Add new paragraphs
      if (newBlocks.length > 0) {
        onAddNewParagraphs(blockId, newBlocks);
      }
      inputRef.current?.blur();
      return true;
    }
    return false;
  };

  const handleFocusOnBlock = () => {
    onFocusingBlock(blockId);
    inputRef.current?.focus();
  };

  const handleDownloadBlockAudio = async () => {
    if (isDisablePlayAudio) return;
    const response = await getPresignedAudioUrl(requestId);
    if (response?.status) {
      const audioUrl = response.result?.audio;
      const audioName = getFileName(audioLink);
      downloadFileByUrl(audioUrl, `${indexBlock}_${audioName}`);
      trackBlockAudioDownloaded({ requestId });
    }
  };

  const handleOpenErrorReport = () => {
    const ticketMetadata = {
      requestId,
      voiceCode,
      userId,
      blockId,
      projectId,
    };
    const base64TicketMetadata = btoa(JSON.stringify(ticketMetadata));
    const encodedTicketMetadata = encodeURIComponent(base64TicketMetadata);

    window.open(
      `${LANDING_PAGE_URL}/support?metadata=${encodedTicketMetadata}&platform=${PLATFORM_TYPE.DESKTOP_WEB}&category=${CATEGORY_TYPE.PROJECT}`,
      '_blank',
    );
  };

  useEffect(() => {
    if (text) {
      const contentEditor = EditorState.createWithContent(
        ContentState.createFromText(text),
        generateDecorator(BREAK_TIME_REGEX, BreakTimeHighlight),
      );
      handleChangeContent(contentEditor);
    }
  }, []);

  useEffect(() => {
    if (isFocusing) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isFocusing]);

  useEffect(() => {
    if (!isFocusing) return undefined;

    const handleClickOutside = (e) => {
      if (
        blockRef.current &&
        !blockRef.current.contains(e.target) &&
        !e.target.closest(
          '.MuiPopover-root, .MuiModal-root, .MuiDialog-root, .MuiPopper-root, .MuiPaper-root , .rc-tooltip, [data-no-blur-block]',
        )
      ) {
        onFocusingBlock(null);
        inputRef.current?.blur();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isFocusing]);

  useEffect(() => {
    dispatch(actions.project.updateBlockStatus({ blockId, blockStatus }));
  }, [blockStatus]);

  useEffect(() => {
    const currentBrowser = checkBrowser();
    const decorators = [
      { regex: BREAK_TIME_REGEX, component: BreakTimeHighlight },
    ];

    if (foundBlackWords.length > 0) {
      const blackWordsRegex = foundBlackWords.map((bw) =>
        bw.replaceAll(' ', ' +'),
      );
      const blackWordPattern = !currentBrowser?.safariAgent
        ? new RegExp(
            `(?<![${VALID_LETTERS}])(${blackWordsRegex.join(
              '|',
            )})(?![${VALID_LETTERS}])`,
            'gi',
          )
        : new RegExp(
            `(^|[^${VALID_LETTERS}])(${blackWordsRegex.join(
              '|',
            )})([^${VALID_LETTERS}]|$)`,
            'gi',
          );
      decorators.push({
        regex: blackWordPattern,
        component: BlackWordHighLight,
      });
    }

    setContent((prev) =>
      EditorState.set(prev, {
        decorator: generateCompositeDecorator(decorators),
      }),
    );
  }, [foundBlackWords.join(',')]);

  const selectedBlockIds = useSelector(
    (state) => state.project.selectedBlockIds,
  );
  const isSelected = useMemo(
    () => selectedBlockIds.includes(blockId),
    [selectedBlockIds, blockId],
  );

  const renderConfigBlock = () => (
    <ConfigOptionBlock
      handleOpenVoices={handleOpenVoices}
      handleChangeSpeed={handleChangeSpeed}
      handleChangeBreakTime={handleChangeBreakTime}
      handleChangeEmphasis={handleChangeEmphasis}
      voice={voice}
      speed={speed}
      breakTime={breakTime}
      hasEmphasis={hasEmphasis}
      emphasis={emphasis}
      isSynthesizing={isSynthesizing}
      onSplitBlock={handleSplitBlock}
      canSplitBlock={canSplitBlock}
      isBlockActive={isFocusing || isSelected}
      isBlockUIImprovement
    />
  );
  const renderCharacterCount = () => (
    <CreditsCount
      creditFactor={voice?.creditFactor}
      exceedBlockMaxCredits={exceedBlockMaxChar}
      credits={countTextLength(text)}
      maxCredits={maxCharLimit}
    />
  );

  const handleSelectBlock = () => {
    if (isSelected) {
      dispatch(actions.project.unselectBlocks([blockId]));
      return;
    }
    dispatch(actions.project.selectBlocks([blockId]));
  };

  const handleMergeBlock = () => {
    const mergedText = onMergeBlock(blockId);
    if (mergedText) {
      // Update editor state for current block
      const newContentState = ContentState.createFromText(mergedText);
      const newEditorState = EditorState.createWithContent(
        newContentState,
        generateDecorator(BREAK_TIME_REGEX, BreakTimeHighlight),
      );
      handleChangeContent(newEditorState);
    }
  };

  const renderSelectBlock = () => (
    <>
      <div className="select-block select-block-new-ui-v2">
        <Checkbox
          checked={isSelected}
          onChange={handleSelectBlock}
          className="checkbox-color"
        />
      </div>
    </>
  );

  const renderMergeBlockTooltip = () => {
    if (!areBlocksConsecutive)
      return (
        <NewTypography
          variant="body-14-medium"
          dangerouslySetInnerHTML={{
            __html: sanitize(t('cannotMergeBecauseNotConsecutive'), {
              allowedTags: ['span', 'br'],
            }),
          }}
        />
      );

    if (isNextBlockSynthesizing || isSynthesizing)
      return (
        <NewTypography
          variant="body-14-medium"
          dangerouslySetInnerHTML={{
            __html: sanitize(t('cannotMergeBecauseExistBlockIsSynthesizing'), {
              allowedTags: ['span', 'br'],
            }),
          }}
        />
      );

    return null;
  };

  const renderMergeBlockButton = () => (
    <span style={{ display: 'inline-block' }}>
      <CommonButton
        onClick={handleMergeBlock}
        variant="transparent"
        disabled={cannotMerge}
        startIcon={<MergeIcon sx={{ width: '20px', height: '20px' }} />}
        customStyle={{
          height: '24px',
          textTransform: 'none',
          borderRadius: '480px',
          border: `1px solid ${COLOR.indigo[32]}`,
          backgroundColor: COLOR.white,
          fontWeight: 500,
          fontSize: '12px',
          lineHeight: '16px',
          letterSpacing: '-0.4px',
          textAlign: 'center',
          verticalAlign: 'middle',
          padding: '4px 16px',
          '&:hover': {
            backgroundColor: COLOR.whiteV2['64'],
            boxShadow: 'none',
          },
        }}
        textProps={{
          variantText: 'body-12-medium',
          color: COLOR.indigo,
        }}
      >
        {t('mergeBlock')}
      </CommonButton>
    </span>
  );

  const getBlockBorderColor = ({ isError, isActive }) => {
    if (isError) return COLOR.functionalRed.default;
    if (isActive) return COLOR.indigo.default;
    return COLOR.label['16'];
  };

  return (
    <StyledContentBlockWrapper>
      <StyledContentBlock
        isLastParagraphId={isLastBlock}
        isFocusingOnBlock={isHighlight || isSelected}
        isError={
          exceedBlockMaxChar ||
          hasInvalidCharacterLength ||
          hasInvalidCharacters ||
          hasBlackWords
        }
        isHighlight={isHighlight}
      >
        <StyledOrdinalNumberBlock>
          {/* Time Frame Row for Dubbing Projects */}

          <StyledInfoBlock>
            <NewTypography
              color={COLOR.descriptionUpload}
              variant="body-14-regular"
            >
              {indexBlock}.
            </NewTypography>

            <EditNameBlock blockId={blockId} title={title} />
          </StyledInfoBlock>

          <div
            className="block-wrapper block-wrapper-v2"
            ref={blockRef}
            role="presentation"
            style={{
              border: `0.5px solid ${getBlockBorderColor({
                isError:
                  exceedBlockMaxChar ||
                  hasInvalidCharacterLength ||
                  hasInvalidCharacters ||
                  hasBlackWords,
                isActive: isHighlight || isSelected,
              })}`,
            }}
          >
            <div
              className="block-toolbar-wrapper"
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: 0,
                width: '100%',
                boxSizing: 'border-box',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  flex: 1,
                  gap: '8px',
                }}
              >
                {renderSelectBlock()}
                <div className="block-header-divider" />
                {renderConfigBlock()}
              </div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  flexShrink: 0,
                }}
              >
                {(hasInvalidCharacterLength ||
                  hasInvalidCharacters ||
                  exceedBlockMaxChar ||
                  hasBlackWords) && (
                  <Tooltip
                    arrow
                    title={
                      <Typography
                        component="div"
                        sx={{
                          fontSize: '14px',
                          textAlign: 'left',
                          display: 'block',
                        }}
                        dangerouslySetInnerHTML={{
                          __html: (() => {
                            const { message, sanitizeOptions } =
                              getBlockErrorConfig();
                            return sanitize(message, sanitizeOptions);
                          })(),
                        }}
                      />
                    }
                    placement="left"
                  >
                    <div
                      style={{
                        width: '40px',
                        height: '40px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                      className="exceed-block-char-icon"
                    >
                      <WarningRoundedIcon
                        sx={{ color: COLOR.danger, fontSize: '24px' }}
                      />
                    </div>
                  </Tooltip>
                )}
                <NewSynthesisButton
                  blockId={blockId}
                  status={status}
                  isPlaying={isPlayingBlock}
                  isDisableConvertBtn={isDisableConvertBtn}
                  onSaveProject={onSaveProject}
                  showSuccessIcon={showSuccessIcon}
                  audioExpiredAt={audioExpiredAt}
                  onOpenOutOfCreditsDialog={onOpenOutOfCreditsDialog}
                  onOpenWrongSyntaxDialog={onOpenWrongSyntaxDialog}
                  onOpenWrongSyntaxDialogV2={onOpenWrongSyntaxDialogV2}
                  checkValidCreateSynthesis={checkValidCreateSynthesis}
                  handlePlayAudio={handlePlayAudio}
                  onOpenDiscardedVoiceCloningDialog={
                    onOpenDiscardedVoiceCloningDialog
                  }
                  isDisablePlayAudio={isDisablePlayAudio}
                  onConvertBlockToAudio={onConvertBlockToAudio}
                  isBlockEditting={isBlockEditting}
                  enableCreditUsagePreview={enableCreditUsagePreview}
                  isBlockUIImprovement
                  showAudioPopover={showReSynthesize}
                  audioLink={audioLink}
                  popoverCurrentTime={popoverCurrentTime}
                  popoverDuration={popoverDuration}
                  onSeekInBlock={handleSeekedInBlock}
                />
                <MoreOptionBlock
                  openMoreOptionBlock={openPopperBlockOption}
                  onOpenDeleteDialog={handleOpenDeleteDialog}
                  onOpenMoreOptionBlock={setOpenPopperBlockOption}
                  onOpenReportDialog={handleOpenErrorReport}
                  isDisabledDownloadBlockAudio={isDisablePlayAudio}
                  onDownloadBlockAudio={handleDownloadBlockAudio}
                  isDisabledDeleteBlock={false}
                  onMoveBlockUp={() => onDragEnd(blockId, DIRECTION.UP)}
                  onMoveBlockDown={() => onDragEnd(blockId, DIRECTION.DOWN)}
                  onDuplicateBlock={() => onDuplicateBlock(blockId)}
                  onReSynthesizeBlock={() => onReSynthesizeBlock(blockId)}
                  isFirstBlock={disabledMoveUp}
                  isLastBlock={isLastBlock}
                  showReSynthesize={showReSynthesize}
                  isDisabledMoveBlock={isPlayingBlock}
                  isDisabledReSynthesize={isDisableConvertBtn}
                  reSynthesizeTooltip={reSynthesizeTooltip}
                />
              </div>
            </div>

            {/* Text editor */}
            <div className="editor-wrapper editor-wrapper-new-ui">
              <StyledBlockEditor
                onClick={handleFocusOnBlock}
                tabIndex="-1"
                isFocusingOnBlock={isFocusing}
              >
                <div
                  className={classNames('editor-container', {
                    'character-exceed': characterExceed,
                  })}
                >
                  <Editor
                    placeholder={t('enterTextHere')}
                    editorState={content}
                    handlePastedText={handlePastedText}
                    handleKeyCommand={handleKeyCommand}
                    keyBindingFn={keyBindingFn}
                    stripPastedStyles
                    onChange={handleEditorChange}
                    ref={inputRef}
                    readOnly={isSynthesizing}
                  />
                </div>
              </StyledBlockEditor>
            </div>

            {/* Character count ở dưới text */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                padding: 0,
                width: '100%',
                boxSizing: 'border-box',
              }}
            >
              {renderCharacterCount()}
            </div>
          </div>
        </StyledOrdinalNumberBlock>
        {openVoices && (
          <VoiceSelectionDialog
            open={openVoices}
            activeVoiceId={voice && voice.id}
            activeVoiceCode={voice && voice.code}
            voiceStatus={voice && voice.status}
            onClose={handleCloseVoices}
            onChangeVoice={handleChangeVoice}
            hasLanguageFilter
          />
        )}
      </StyledContentBlock>
      {!isLastBlock && (
        <StyledAddNewBlockZone>
          <div className="zone-wrapper">
            <Typography
              variant="body2"
              color="text.secondary"
              className="command-to-add-new-block"
            >
              {t('commandToAddNewBlock')}
            </Typography>
            <hr className="border" />
            <>
              {cannotMerge ? (
                <Tooltip
                  arrow
                  title={renderMergeBlockTooltip()}
                  placement="top"
                  maxWidth="550px"
                >
                  {renderMergeBlockButton()}
                </Tooltip>
              ) : (
                renderMergeBlockButton()
              )}
            </>
            <Button
              onClick={handleAddNewParagraphs}
              className="add-btn"
              variant="contained"
              startIcon={
                <img className="add-btn-icon" src={addBlock} alt="icon" />
              }
            >
              {t('addMoreBlock')}
            </Button>
          </div>
        </StyledAddNewBlockZone>
      )}
    </StyledContentBlockWrapper>
  );
};

function arePropsEqual(prevProps, nextProps) {
  return (
    prevProps.blockId === nextProps.blockId &&
    prevProps.characterExceed === nextProps.characterExceed &&
    prevProps.isLastBlock === nextProps.isLastBlock &&
    prevProps.isFocusing === nextProps.isFocusing &&
    prevProps.block === nextProps.block &&
    prevProps.disabledMoveUp === nextProps.disabledMoveUp &&
    prevProps.voiceDefault === nextProps.voiceDefault &&
    prevProps.listVoice === nextProps.listVoice &&
    prevProps.isPlaying === nextProps.isPlaying &&
    prevProps.isDownloading === nextProps.isDownloading &&
    prevProps.isHighlight === nextProps.isHighlight &&
    prevProps.projectId === nextProps.projectId &&
    prevProps.isPlayingBlock === nextProps.isPlayingBlock &&
    prevProps.originalBlock === nextProps.originalBlock &&
    prevProps.audioType === nextProps.audioType &&
    prevProps.nextBlock === nextProps.nextBlock &&
    prevProps.prevBlock === nextProps.prevBlock &&
    prevProps.blockAudioCurrentTime === nextProps.blockAudioCurrentTime &&
    prevProps.blockAudioDuration === nextProps.blockAudioDuration &&
    prevProps.currentPlayingBlockId === nextProps.currentPlayingBlockId &&
    prevProps.foundBlackWords?.join(',') ===
      nextProps.foundBlackWords?.join(',')
  );
}

export default memo(ContentBlock, arePropsEqual);
